import { Injectable } from '@angular/core';
import { ApiService } from '../services/api.service';
import { BehaviorSubject, Observable, of, shareReplay, tap } from 'rxjs';
import { User } from '../authentication/auth.models';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly USER_STORAGE_KEY = 'enjochem_user_profile';
  private _userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(this._getUserFromStorage());
  public currentUser$: Observable<User | null> = this._userSubject.asObservable();

  private _baseUrl = 'auth/users/me/';
  private _pendingRequest$: Observable<User> | null = null;
  private _lastFetchedAt: number | null = null;
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(private _apiService: ApiService) {}

  get(): Observable<User> {
    const currentUser = this._userSubject.value;
    if (currentUser && this._lastFetchedAt && Date.now() - this._lastFetchedAt < this.CACHE_TTL_MS) {
      return of(currentUser);
    }

    if (!this._pendingRequest$) {
      this._pendingRequest$ = this._apiService.get<User>(this._baseUrl).pipe(
        tap((user: User) => {
          localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
          this._userSubject.next(user);
          this._lastFetchedAt = Date.now();
        }),
        shareReplay(1)
      );
      this._pendingRequest$.subscribe({
        complete: () => (this._pendingRequest$ = null),
        error: () => (this._pendingRequest$ = null),
      });
    }
    return this._pendingRequest$;
  }

  patch(reqBody: any): Observable<User> {
    return this._apiService.patch<User>(this._baseUrl, reqBody).pipe(
      tap((user: User) => {
        localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
        this._userSubject.next(user);
      })
    );
  }

  getCurrentUser(): User | null {
    return this._userSubject.value;
  }

  clearUser(): void {
    localStorage.removeItem(this.USER_STORAGE_KEY);
    this._userSubject.next(null);
    this._lastFetchedAt = null;
  }

  private _getUserFromStorage(): User | null {
    try {
      const stored = localStorage.getItem(this.USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Error parsing stored user', e);
      return null;
    }
  }
}
